// Every API request goes through fetchWithLogging: a time limit, and an
// entry in the diagnostics API log that bug reports carry.
import { recordApiCall, redactString, redactUrl } from "./utils/diagnostics";

/** A JSON call's limit: long enough for a cold Lambda, short enough to say so. */
export const DEFAULT_TIMEOUT_MS = 8_000;

/** The request ran past its time limit. Its message is shown to the player as is. */
export class FetchTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super("The server didn't answer in time. Check your connection and try again.");
    this.name = "FetchTimeoutError";
  }
}

export interface FetchOptions {
  /** Milliseconds before the request is cancelled, or null for no limit. Defaults to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number | null;
}

/**
 * A signal that aborts after `ms`. An abort cancels the request itself;
 * racing a promise against a timer would reject the caller and leave the
 * connection running. The fallback covers anywhere AbortSignal.timeout is
 * missing.
 */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(new DOMException("The operation timed out.", "TimeoutError")), ms);
  return controller.signal;
}

/** True for the rejection a timed-out or cancelled fetch produces. */
export function isAbort(err: unknown): boolean {
  const name = (err as { name?: string })?.name;
  if (name === "AbortError" || name === "TimeoutError" || err instanceof FetchTimeoutError) return true;
  const message = (err as { message?: string })?.message ?? "";
  return /abort/i.test(message);
}

/** The caller's signal and the time limit, as one. */
function combine(signals: AbortSignal[]): AbortSignal {
  if (signals.length === 1) return signals[0];
  if (typeof AbortSignal.any === "function") return AbortSignal.any(signals);
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

/**
 * fetch, recorded in the API log (method, redacted URL, status, time; never
 * a header or a body), with a time limit. The limit also covers reading the
 * body, since the signal stays attached to the response. A timeout rejects
 * with FetchTimeoutError; the caller's own abort rejects as fetch would.
 */
export async function fetchWithLogging(
  input: RequestInfo | URL,
  init?: RequestInit,
  { timeoutMs = DEFAULT_TIMEOUT_MS }: FetchOptions = {},
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const method = (init?.method || 'GET').toUpperCase();
  const start = Date.now();
  const startTime = performance.now();
  const timer = timeoutMs === null ? null : timeoutSignal(timeoutMs);
  const signals = [init?.signal, timer].filter((s): s is AbortSignal => !!s);
  const signal = signals.length ? combine(signals) : undefined;

  try {
    const response = await fetch(input, signal ? { ...init, signal } : init);
    recordApiCall({
      timestamp: start,
      method,
      path: redactUrl(url),
      status: response.status,
      durationMs: Math.round(performance.now() - startTime),
      error: response.ok ? null : `${response.status}`,
    });
    return response;
  } catch (error) {
    // The limit ran out, not the caller's patience.
    const timedOut = !!timer?.aborted && !init?.signal?.aborted && timeoutMs !== null;
    const thrown = timedOut ? new FetchTimeoutError(timeoutMs) : error;
    recordApiCall({
      timestamp: start,
      method,
      path: redactUrl(url),
      status: null,
      durationMs: Math.round(performance.now() - startTime),
      error: redactString(thrown instanceof Error ? thrown.message : String(thrown)),
    });
    throw thrown;
  }
}
